import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import styles from './PicksSections.module.scss';

/**
 * A rail shows this many cards in the app (the backend's RAIL_ITEM_LIMIT), so
 * it is also the smallest Max in App: a lower cap would leave the rail half
 * empty. Keep in step with `MIN_DAILY_LIMIT` in the backend's config/rails.js.
 */
export const MIN_DAILY_LIMIT = 12;

/**
 * The "Max in App" cell, shared by the Picks and Summary sections tables:
 * how many of a section a reader can reach in one refresh period, the rail and
 * See All together. Blank means no cap; otherwise at least MIN_DAILY_LIMIT,
 * with no upper bound.
 *
 * Holds its own draft so typing doesn't fire a request per keystroke; commits
 * on blur or Enter, and Escape puts the saved value back.
 *
 * `min` / `allowBlank` / `invalidMessage` let the same cell edit a number with
 * other rules — For You's "Books for each reader" (1 or more, never blank).
 */
function DailyLimitInput({
    value,
    disabled,
    placeholder = "All",
    title,
    onCommit,
    min = MIN_DAILY_LIMIT,
    allowBlank = true,
    invalidMessage = `Max in App must be at least ${MIN_DAILY_LIMIT}, or blank for no cap.`,
}) {
    const saved = value > 0 ? String(value) : "";
    const [draft, setDraft] = useState(saved);
    // Set by Escape so the blur that follows doesn't commit: the blur handler
    // still sees the typed draft, since the reset hasn't rendered yet.
    const cancelled = useRef(false);

    useEffect(() => setDraft(saved), [saved]);

    const commit = () => {
        if (cancelled.current) {
            cancelled.current = false;
            return;
        }
        const next = draft.trim() === "" ? 0 : Number(draft);
        const blank = next === 0;
        if (!Number.isInteger(next) || next < 0 || (blank ? !allowBlank : next < min)) {
            setDraft(saved);
            toast.error(invalidMessage);
            return;
        }
        if (next !== (value > 0 ? value : 0)) onCommit(next);
    };

    return (
        <input
            type="text"
            inputMode="numeric"
            className={styles.limitInput}
            value={draft}
            placeholder={placeholder}
            title={title}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                    cancelled.current = true;
                    setDraft(saved);
                    e.currentTarget.blur();
                }
            }}
        />
    );
}

export default DailyLimitInput;
